import { useEffect, useRef, type ReactNode } from 'react';
import { MapPin, Trash2, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSubmitVehicleCapacity } from '~/hooks/useSubmitVehicleCapacity';
import { useCannedTallyGroup } from '~/hooks/useCannedTallyGroup';
import {
  vehicleTypeToTransportMode,
  useAutoSelectTransportVehicle,
  useTransportState,
} from '~/hooks/useTransportState';
import {
  useCartTransportSave,
  useRetryTransportSaveWhen,
} from '~/hooks/useCartTransportSave';
import type {
  Facility,
  LogisticsIncoterm,
  OrderItem,
  Organization,
  TransportVehicle,
} from '~/graphql/graphql';
import {
  calcSupportedVehicleIds,
  getFirstAvailableVehicle,
  getItemFt,
  getItemWholeFt,
  getUnitsPerBundle,
  getVehiclePayload,
  initialDeliveryMethod,
  isVehicleCapacityCheckedLine,
  linesWithoutCapacityFor,
  productNamesOf,
} from '~/utils/order';
import { TransportCard } from '~/components/cards/TransportCard.tsx';
import { TransportSaveFailedBanner } from '~/components/ui/TransportSaveFailedBanner';
import { VehicleCapacityEditor } from '~/components/ui/VehicleCapacityEditor';
import { TallyCountStepper } from '~/components/ui/TallyCountStepper';
import { LoadsCoveredLabel } from '~/components/ui';
import { CANNED_TALLY_VEHICLE_NAMES } from '~/utils/vehicleGroups';
import { useGetTransportVehicles } from '~/api/facility';
import { formatCurrency, sellerLabel } from '~/utils';
import { facilityAddressCodeLabel } from '~/utils/facilityOptions';

export type CannedTallyCartProps = {
  organization: Organization;
  items: OrderItem[];
  sellerOrganizationFacility: Facility;
  shippingLocationFacility: Facility;
  shippingLocationId: string;
  id: string;
  parentOrderId?: string | null;
  isFromQuote: boolean;
  incoTerm?: LogisticsIncoterm | null;
  transportVehicle?: Pick<TransportVehicle, 'id' | 'vehicleType'> | null;
  platformProductId?: string;
  calculatorTransportMode?: 'trucks' | 'railcars';
  currency?: string | null;
  inheritedIncoTerm: LogisticsIncoterm | null;
};

export function CannedTallyCartCard({
  organization,
  items,
  sellerOrganizationFacility,
  shippingLocationFacility,
  shippingLocationId,
  id,
  parentOrderId,
  isFromQuote,
  incoTerm: savedIncoTerm,
  transportVehicle: savedTransportVehicle,
  platformProductId,
  calculatorTransportMode,
  currency: cartCurrency,
  inheritedIncoTerm,
}: Readonly<CannedTallyCartProps>) {
  const { t } = useTranslation(['order-builder', 'cart']);
  const {
    failure: transportSaveFailure,
    save: saveTransport,
    retry: retryTransport,
    isSaving: isTransportSaving,
  } = useCartTransportSave(id);
  const submitVehicleCapacity = useSubmitVehicleCapacity();

  const { data: vehiclesData } = useGetTransportVehicles();
  const cannedTallyVehicles = (vehiclesData?.transportVehicles ?? []).filter(
    (v) => CANNED_TALLY_VEHICLE_NAMES.includes(v.name)
  );

  const {
    transport,
    deliveryMethod,
    selectedTransportVehicleId,
    handleTransportChange,
  } = useTransportState(saveTransport, {
    deliveryMethod: initialDeliveryMethod({
      isInternational: false,
      savedIncoTerm,
      hasSavedVehicle: !!savedTransportVehicle,
      inheritedIncoTerm,
    }),
    selectedTransportVehicleId: savedTransportVehicle?.id,
    transport: savedTransportVehicle
      ? vehicleTypeToTransportMode(savedTransportVehicle.vehicleType)
      : undefined,
  });

  const filteredCannedTallyVehicleIds = cannedTallyVehicles
    .filter((v) => {
      if (calculatorTransportMode === 'railcars')
        return v.vehicleType === 'DOMESTIC_RAIL';
      if (calculatorTransportMode === 'trucks')
        return v.vehicleType === 'DOMESTIC_TRUCK';
      return true;
    })
    .map((v) => v.id);

  const supportedVehicleIds = calcSupportedVehicleIds(items);
  const firstAvailableVehicle = getFirstAvailableVehicle(
    cannedTallyVehicles,
    filteredCannedTallyVehicleIds.length > 0
      ? filteredCannedTallyVehicleIds
      : undefined
  );
  useAutoSelectTransportVehicle(
    firstAvailableVehicle ? [firstAvailableVehicle] : undefined,
    selectedTransportVehicleId,
    handleTransportChange,
    deliveryMethod
  );

  // When the calculator's transport mode changes, auto-select the matching vehicle.
  const prevCalculatorModeRef = useRef(calculatorTransportMode);
  useEffect(() => {
    if (
      calculatorTransportMode == null ||
      calculatorTransportMode === prevCalculatorModeRef.current ||
      cannedTallyVehicles.length === 0
    ) {
      prevCalculatorModeRef.current = calculatorTransportMode;
      return;
    }
    prevCalculatorModeRef.current = calculatorTransportMode;
    const targetType =
      calculatorTransportMode === 'railcars'
        ? 'DOMESTIC_RAIL'
        : 'DOMESTIC_TRUCK';
    const match = cannedTallyVehicles.find((v) => v.vehicleType === targetType);
    if (match) {
      handleTransportChange(deliveryMethod, match.id, match.vehicleType);
    }
  }, [
    calculatorTransportMode,
    cannedTallyVehicles,
    deliveryMethod,
    handleTransportChange,
  ]);

  const effectiveVehicleId =
    selectedTransportVehicleId || savedTransportVehicle?.id || '';

  const {
    tally,
    tallyBundlesByFt,
    tallyItems,
    standaloneItems,
    tallyCount,
    changeTallyCount,
    loadsCovered,
    isUpdating,
    removeTallyGroup,
  } = useCannedTallyGroup({
    items,
    tallyPlatformProductId: platformProductId,
    organizationId: organization.id,
    transportVehicleId: effectiveVehicleId,
    cartId: id,
    locationId: shippingLocationId,
    sellerOrganizationFacilityId: sellerOrganizationFacility.id,
    parentOrderId,
    isFromQuote,
  });

  const retryTransportSave = () => {
    if (effectiveVehicleId) retryTransport(deliveryMethod, effectiveVehicleId);
  };

  const failedVehicleNowHasCapacity =
    transportSaveFailure.kind === 'missing-capacity' &&
    !!effectiveVehicleId &&
    linesWithoutCapacityFor(items, effectiveVehicleId).length === 0;
  useRetryTransportSaveWhen(failedVehicleNowHasCapacity, retryTransportSave);

  const capacityControlFor = (item: OrderItem): ReactNode => {
    if (!isVehicleCapacityCheckedLine(item)) return undefined;
    const submitCapacity = (value: number) =>
      submitVehicleCapacity({
        platformProductLengthId: item.platformProductsLengths.id,
        transportVehicleId: effectiveVehicleId,
        value,
        unit: item.quantity.unit,
      });
    return (
      <VehicleCapacityEditor
        platformProductsLength={item.platformProductsLengths}
        quantityUnit={item.quantity.unit}
        vehiclePayload={getVehiclePayload(
          item.platformProductsLengths.platformProductVehicleCapacities,
          effectiveVehicleId
        )}
        vehicleLabel={transport}
        onCapacitySubmit={submitCapacity}
        disabled={isFromQuote || !effectiveVehicleId}
      />
    );
  };

  const totalPrice = items.reduce(
    (sum, item) =>
      sum + (item.effectivePrice?.amount ?? 0) * item.quantity.value,
    0
  );
  const currency = cartCurrency ?? undefined;

  return (
    <div className="bg-neut-light border border-neut-border rounded-lg overflow-hidden">
      {/* Canned Tally badge */}
      <div className="bg-status-info-50 px-3 py-1 border-b border-status-info-200">
        <span
          data-testid="canned-tally-badge"
          className="text-xs font-semibold text-status-info-800"
        >
          {t('order_builder.filters.canned_tally')}
        </span>
      </div>

      {/* Seller info */}
      <div className="bg-neut-50 px-3 py-2 border-b border-neut-border">
        <p className="text-xs font-semibold text-neut-900">
          {sellerLabel(organization)}
        </p>
        <div className="flex items-center gap-1 mt-0.5">
          <MapPin size={10} className="text-neut-400" aria-hidden="true" />
          <p className="text-xs text-neut-500">
            {facilityAddressCodeLabel(sellerOrganizationFacility)}
          </p>
        </div>
        {!effectiveVehicleId && (
          <p className="text-[10px] text-neut-400 italic mt-1">
            {t('cart.select_transport_mode')}
          </p>
        )}
      </div>

      <TransportSaveFailedBanner
        failure={transportSaveFailure}
        onRetry={retryTransportSave}
        productsNeedingCapacity={productNamesOf(
          linesWithoutCapacityFor(items, effectiveVehicleId)
        )}
        isSaving={isTransportSaving}
        className="mx-3 mt-1.5"
      />

      {/* Delivery method + transport mode */}
      <TransportCard
        sellerOrganizationFacility={sellerOrganizationFacility}
        shippingLocationFacility={shippingLocationFacility}
        deliveryMethod={deliveryMethod}
        selectedTransportVehicleId={selectedTransportVehicleId}
        supportedVehicleIds={
          filteredCannedTallyVehicleIds.length > 0
            ? filteredCannedTallyVehicleIds
            : supportedVehicleIds
        }
        transportVehicleCatalog={cannedTallyVehicles}
        onTransportChange={handleTransportChange}
      />

      {/* Tally count + trucks covered + total price */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-neut-100">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <TallyCountStepper
              value={tallyCount}
              onChange={changeTallyCount}
              disabled={isUpdating || !tally}
              min={1}
            />
            <span className="text-xs text-neut-600 ml-1">
              {t('order_builder.canned_tally_calculator.tallies').toLowerCase()}
            </span>
          </div>
          <LoadsCoveredLabel
            loadsCovered={
              transportSaveFailure.kind === 'none' && !isTransportSaving
                ? loadsCovered
                : null
            }
            transport={transport}
          />
        </div>
        <p className="text-sm font-semibold text-neut-900">
          {formatCurrency(totalPrice, currency)}
        </p>
      </div>

      {/* Tally group items */}
      {tallyItems.length > 0 && (
        <div className="divide-y divide-neut-100">
          {tallyItems.map((item) => {
            const ft = getItemFt(item);
            const wholeFt = getItemWholeFt(item);
            const unitsPerBundle = getUnitsPerBundle(item);
            const tallyBundles =
              wholeFt === null ? undefined : tallyBundlesByFt[wholeFt];
            return (
              <CartLengthRow
                key={item.platformProductsLengths?.id}
                item={item}
                ft={ft}
                unitsPerBundle={unitsPerBundle}
                bundleCount={
                  tallyBundles != null ? tallyCount * tallyBundles : undefined
                }
                currency={currency}
                capacityControl={capacityControlFor(item)}
              />
            );
          })}
        </div>
      )}

      {/* A single length within a tally cannot be edited here. */}
      {standaloneItems.length > 0 && (
        <div className="divide-y divide-neut-100 border-t border-neut-border">
          {standaloneItems.map((item) => (
            <CartLengthRow
              key={item.platformProductsLengths?.id}
              item={item}
              ft={getItemFt(item)}
              unitsPerBundle={getUnitsPerBundle(item)}
              hint={
                isVehicleCapacityCheckedLine(item)
                  ? undefined
                  : t(
                      'order_builder.canned_tally_calculator.standalone_length_hint'
                    )
              }
              currency={currency}
              capacityControl={capacityControlFor(item)}
            />
          ))}
        </div>
      )}

      {(tallyItems.length > 0 || standaloneItems.length > 0) && (
        <button
          type="button"
          data-testid="remove-tally-group"
          onClick={removeTallyGroup}
          className="w-full py-1.5 text-xs text-error-600 hover:bg-error-50 border-t border-neut-100 transition-colors flex items-center justify-center gap-1"
        >
          <Trash2 size={12} aria-hidden="true" />
          {t('order_builder.canned_tally_calculator.remove_tally_group')}
        </button>
      )}
    </div>
  );
}

function CartLengthRow({
  item,
  ft,
  unitsPerBundle,
  bundleCount,
  hint,
  currency,
  capacityControl,
}: Readonly<{
  item: OrderItem;
  ft: number;
  unitsPerBundle: number | null;
  bundleCount?: number;
  hint?: string;
  currency?: string;
  capacityControl?: ReactNode;
}>) {
  const { t } = useTranslation('order-builder');
  return (
    <div className="p-3">
      <div className="mb-2">
        <span className="inline-flex items-center px-2 py-0.5 bg-status-info-50 text-status-info-800 border border-status-info-200 rounded text-xs font-medium">
          {ft}
          {t('order_builder.canned_tally_calculator.ft')}
        </span>
        <p className="text-xs text-neut-700 mt-1">
          {item.platformProductsLengths?.platformProduct?.name} - {ft}
          {t('order_builder.canned_tally_calculator.ft')}
        </p>
        {unitsPerBundle != null && (
          <p className="text-[10px] text-neut-500 mt-0.5 flex items-center gap-1">
            <Clock size={10} aria-hidden="true" />
            {unitsPerBundle.toFixed(2)} {item.quantity.unit}/
            {t('order_builder.inventory.bundle')}
          </p>
        )}
        {hint != null && (
          <p className="text-[10px] text-warning-600 mt-0.5">{hint}</p>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-neut-600">
          {item.quantity.value.toFixed(2)} {item.quantity.unit}
        </span>
        <div className="text-right">
          <p className="text-xs text-neut-500">
            {formatCurrency(item.effectivePrice?.amount ?? 0, currency)}/
            {item.quantity.unit}
          </p>
          <p className="text-sm font-semibold text-neut-900">
            {formatCurrency(
              (item.effectivePrice?.amount ?? 0) * item.quantity.value,
              currency
            )}
          </p>
          {bundleCount != null && (
            <p className="text-[10px] text-neut-400 mt-0.5">
              {bundleCount} {t('order_builder.canned_tally_calculator.bundles')}
            </p>
          )}
        </div>
      </div>
      {capacityControl}
    </div>
  );
}
